import { initialize, LDClient, LDContext } from 'launchdarkly-js-client-sdk';
import {
  LDProvider,
  ProviderConfig,
  useLDClient,
} from 'launchdarkly-react-client-sdk';
import {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { TeacherMe } from '@gmm/sdk/shared';

import { useCurrentTeacher } from '../hooks/useCurrentTeacher';

import { FALLBACK_ANONYMOUS_LD_CONTEXT, toLDContext } from './utils';

interface Props {
  children?: ReactNode;
  clientSideID: string;
}

interface LDReadyGateProps {
  children?: ReactNode;
}

type OwnedClientPhase = 'teacher' | 'fallback';

/** How long to wait for teacher-context client init before opening the app. */
export const LD_INIT_TIMEOUT_MS = 5_000;

/**
 * True while the shared anonymous fallback is active. Survives remounting
 * `LDProvider` so the ready gate does not hide the app again.
 */
export const LDAnonymousFallbackContext = createContext(false);

const ldProviderOptions = {
  bootstrap: 'localStorage' as const,
};

/** Stable `LDProvider` key until anonymous fallback remounts it. */
const LD_PROVIDER_KEY = 'ld-provider';

/**
 * Builds LaunchDarkly React SDK config that does not initialize until a teacher
 * context exists. Passing no context avoids anonymous client-side MAU. The
 * shared anonymous fallback is only for teacher-context init that never
 * becomes ready.
 *
 * @param clientSideID LaunchDarkly client-side ID for the current environment
 * @param teacher Current teacher, or null/undefined before login
 * @param useAnonymousFallback When true, use the shared anonymous context
 * @returns Provider config with deferred initialization
 */
export const getLDProviderConfig = (
  clientSideID: string,
  teacher?: TeacherMe | null,
  useAnonymousFallback = false,
): ProviderConfig => ({
  clientSideID,
  context: useAnonymousFallback
    ? FALLBACK_ANONYMOUS_LD_CONTEXT
    : toLDContext(teacher),
  deferInitialization: true,
  options: ldProviderOptions,
  reactOptions: { useCamelCaseFlagKeys: true },
});

/**
 * Creates a JS SDK v3 client, matching `launchdarkly-react-client-sdk` 3.0.9.
 * `LDProvider` does not close its client on unmount, so this module owns
 * initialize/close.
 *
 * @param clientSideID LaunchDarkly client-side ID for the current environment
 * @param context User context to initialize with
 * @returns The JS SDK client
 */
const createOwnedLdClient = (
  clientSideID: string,
  context: LDContext,
): LDClient => initialize(clientSideID, context, ldProviderOptions);

/**
 * Defers LaunchDarkly client SDK initialization until a teacher is available.
 * Unauthenticated routes must not create anonymous client-side MAU. If the
 * teacher-context client never becomes ready, it is closed and replaced with a
 * shared anonymous client so only one client remains active.
 *
 * @param props.clientSideID LaunchDarkly client-side ID for the current environment
 * @param props.children App tree that may read flags after the teacher is identified
 */
export const LaunchDarklyProvider = memo(function LaunchDarklyProvider({
  children,
  clientSideID,
}: Props) {
  const { data: teacher } = useCurrentTeacher();
  const teacherId = teacher?.id;
  const teacherRef = useRef(teacher);
  const clientPhaseRef = useRef<OwnedClientPhase | null>(null);
  const [fallbackTeacherId, setFallbackTeacherId] = useState<string | null>(
    null,
  );
  const [ldClient, setLdClient] = useState<LDClient | undefined>();
  const useAnonymousFallback = fallbackTeacherId === teacherId;

  teacherRef.current = teacher;

  useEffect(() => {
    if (!teacherId) {
      setLdClient(undefined);
      clientPhaseRef.current = null;

      return;
    }

    const context = useAnonymousFallback
      ? FALLBACK_ANONYMOUS_LD_CONTEXT
      : toLDContext(teacherRef.current);

    if (!context) {
      setLdClient(undefined);
      clientPhaseRef.current = null;

      return;
    }

    const phase: OwnedClientPhase = useAnonymousFallback
      ? 'fallback'
      : 'teacher';
    const client = createOwnedLdClient(clientSideID, context);

    clientPhaseRef.current = phase;
    setLdClient(undefined);

    let cancelled = false;
    let settled = false;
    let timeoutId: number | undefined;

    // LDProvider 3.0.9 must receive an external client only after ready/failed.
    const onReadyPoint = (): void => {
      if (cancelled || settled) {
        return;
      }

      settled = true;

      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
        timeoutId = undefined;
      }

      setLdClient(client);
    };

    client.on('ready', onReadyPoint);
    client.on('failed', onReadyPoint);

    if (!useAnonymousFallback) {
      timeoutId = window.setTimeout(() => {
        if (!cancelled && !settled) {
          setFallbackTeacherId(teacherId);
        }
      }, LD_INIT_TIMEOUT_MS);
    }

    return () => {
      cancelled = true;

      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }

      client.off('ready', onReadyPoint);
      client.off('failed', onReadyPoint);
      void client.close();

      if (clientPhaseRef.current === phase) {
        clientPhaseRef.current = null;
      }
    };
  }, [clientSideID, teacherId, useAnonymousFallback]);

  // LDProvider 3.0.9 already inits deferred context on absent → present, so
  // login must not remount (that remounts Auth and re-runs signIn). Remount
  // only for teacher → anonymous, which the SDK does not initialize.
  const providerKey = useAnonymousFallback
    ? FALLBACK_ANONYMOUS_LD_CONTEXT.key
    : LD_PROVIDER_KEY;
  const clientPhase: OwnedClientPhase = useAnonymousFallback
    ? 'fallback'
    : 'teacher';
  const passedClient =
    clientPhaseRef.current === clientPhase ? ldClient : undefined;
  const passedContext = passedClient
    ? useAnonymousFallback
      ? FALLBACK_ANONYMOUS_LD_CONTEXT
      : toLDContext(teacher)
    : undefined;

  return (
    <LDAnonymousFallbackContext.Provider value={useAnonymousFallback}>
      <LDProvider
        key={providerKey}
        {...getLDProviderConfig(clientSideID, teacher, useAnonymousFallback)}
        context={passedContext}
        ldClient={passedClient}
      >
        {children}
      </LDProvider>
    </LDAnonymousFallbackContext.Provider>
  );
});

/**
 * Holds authenticated children until the deferred client has finished
 * initialization, failed, or timed out. `useLDClient()` becoming non-null only
 * means the instance exists; flags may still be cached/default until
 * `waitForInitialization` settles. Login and OIDC routes stay outside this
 * gate. If LaunchDarkly is unreachable the gate still opens so the app can
 * render with cached/default flags.
 *
 * @param props.children Authenticated app tree that reads feature flags
 */
export const LDReadyGate = memo(function LDReadyGate({
  children,
}: LDReadyGateProps) {
  const { data: teacher } = useCurrentTeacher();
  const ldClient = useLDClient();
  const usingAnonymousFallback = useContext(LDAnonymousFallbackContext);
  const teacherId = teacher?.id;
  const [readyForTeacherId, setReadyForTeacherId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!teacherId) {
      setReadyForTeacherId(null);

      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      if (!cancelled) {
        setReadyForTeacherId(teacherId);
      }
    }, LD_INIT_TIMEOUT_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [teacherId]);

  useEffect(() => {
    if (!teacherId || !ldClient) {
      return;
    }

    let cancelled = false;

    const markReady = (): void => {
      if (!cancelled) {
        setReadyForTeacherId(teacherId);
      }
    };

    void Promise.resolve(ldClient.waitForInitialization())
      .then(markReady)
      .catch(markReady);

    return () => {
      cancelled = true;
    };
  }, [ldClient, teacherId]);

  if (teacherId && readyForTeacherId !== teacherId && !usingAnonymousFallback) {
    return null;
  }

  return <>{children}</>;
});
